<script setup lang="ts">
import { type StyleValue } from 'vue';
import { createPopper, type Instance as PopperInstance } from '@popperjs/core';
import NavIcon from './NavIcon.vue';
import { type NavbarIcon, type NavPosition } from './types';
import { NuxtLink } from '#components';

const {
    handleOverflow,
    to,
    top = '56px',
} = defineProps<{
    position: NavPosition;
    title: string;
    to?: string;
    href?: string;
    icon?: NavbarIcon;
    iconDefaultColor?: string;
    handleOverflow?: boolean;
    top?: string;
}>();

defineOptions({
    inheritAttrs: false,
});

const slots = useSlots();
const attrs = useAttrs();
const hasSubItems = computed(() => !!slots.default);
const hasAction = computed(() => !!attrs.onClick);
const expanded = ref(false);
const maxHeight = ref(0);
const subItemsContainer = ref<HTMLDivElement>();
const itemClicked = (ev: MouseEvent) => {
    if (hasSubItems.value) {
        expanded.value = !expanded.value;
    } else if (hasAction.value) {
        (attrs as any).onClick?.(ev);
    }
};
watch(expanded, value => {
    if (value) {
        maxHeight.value = subItemsContainer.value?.clientHeight || 0;
    }
});

// popper
const subItems = ref<HTMLDivElement>();
const itemButton = ref<HTMLDivElement>();
const popper = ref<PopperInstance>();
onMounted(() => {
    if (hasSubItems.value && handleOverflow) {
        popper.value = createPopper(itemButton.value!, subItems.value!, {
            placement: 'bottom-end',
        });
    }
});
onBeforeUnmount(() => popper.value?.destroy());
</script>

<template>
    <li
        class="item"
        :class="[position, attrs.class]"
        :style="attrs.style as StyleValue"
    >
        <NuxtLink v-if="to" :to class="body">
            <NavIcon
                v-if="icon"
                :icon
                :default-color="iconDefaultColor"
                class="icon"
            />
            <span class="title">{{ title }}</span>
        </NuxtLink>
        <a v-else-if="href" :href class="body">
            <NavIcon
                v-if="icon"
                :icon
                :default-color="iconDefaultColor"
                class="icon"
            />
            <span class="title">{{ title }}</span>
        </a>
        <div
            v-else-if="hasAction || hasSubItems"
            ref="itemButton"
            class="body"
            @click="itemClicked"
        >
            <NavIcon
                v-if="icon"
                :icon
                :default-color="iconDefaultColor"
                class="icon"
            />
            <span class="title">{{ title }}</span>
            <slot v-if="hasSubItems" name="expand" :expanded />
        </div>
        <div
            v-if="hasSubItems"
            ref="subItems"
            class="sub-items"
            :class="{ hidden: !expanded }"
            :style="{ '--max-height': `${maxHeight}px` }"
        >
            <ul ref="subItemsContainer" class="sub-menu">
                <slot />
            </ul>
        </div>
    </li>
</template>

<style scoped lang="scss">
.sidebar.item {
    width: 100%;
    cursor: pointer;
    // border-bottom: 1px solid usePalette($p-cool-gray, 200);
    // &:last-child {
    //     border-bottom: none;
    // }
    .selected {
        .title {
            color: usePalette($p-blue, 400) !important;
        }
        :deep(.mr-icon-svg) {
            color: usePalette($p-blue, 400) !important;
        }
    }
    .body {
        display: flex;
        align-items: center;
        padding-block: 6px;
        &:hover {
            text-decoration: none;
        }
        &.router-link-exact-active {
            .title {
                // color: usePalette() !important;
                color: usePalette($p-blue, 400) !important;
            }
            :deep(.mr-icon-svg) {
                color: usePalette($p-blue, 400) !important;
            }
        }
        .icon {
            margin-left: 0;
        }
        .title {
            @include tg-m($w5);
            margin-right: 12px;
            flex-grow: 1;
        }
    }
    .sub-items {
        overflow: hidden;
        max-height: var(--max-height);
        background-color: usePalette($p-cool-gray, $p-100);
        border-radius: 8px;
        transition: linear 0.2s;
        &.hidden {
            max-height: 0;
        }
        .sub-menu {
            list-style-type: none;
            padding: 16px 12px;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }
    }
}

.drawer.item {
    .body {
        display: flex;
        align-items: center;
        padding-inline: 12px;
        gap: 12px;
        .title {
            @include tg-m($w5);
        }
        .caret {
            margin-right: auto;
            transition: transform 0.3s;
            display: flex;
        }
    }
    .sub-items {
        margin-top: 24px;
        background-color: usePalette($p-cool-gray, $p-100);
        padding: 8px 24px;
        display: flex;
        flex-direction: column;
        gap: 20px;
        transition: linear 0.2s;
        overflow: hidden;
        max-height: calc(var(--max-height) + 16px);
        &.hidden {
            max-height: 0;
            padding-block: 0;
            margin-top: 0;
        }

        .sub-menu {
            display: flex;
            flex-direction: column;
            gap: 20px;
            padding-right: 0;
            background-color: transparent;
            list-style-type: none;
        }
    }
}

.header.item {
    width: auto;
    border: 0 !important;
    white-space: nowrap;
    &:last-child {
        margin-left: 0;
    }
    .body {
        display: flex;
        align-items: center;
        border-radius: 20px;
        width: fit-content;
        padding: 8px;
        color: var(--color) !important;
        font-size: 16px !important;
        text-decoration: none;
        &:hover {
            background-color: rgba($color: black, $alpha: 0.1);
            & + .sub-items {
                visibility: visible;
                opacity: 1;
                -webkit-transition-delay: 0s;
                -moz-transition-delay: 0s;
                -ms-transition-delay: 0s;
                -o-transition-delay: 0s;
                transition-delay: 0s;
            }
        }
        .icon {
            margin: 0;
            &:first-child {
                margin-left: 8px;
            }
            &:not(:first-child) {
                margin-right: 8px;
            }
        }
    }
    .sub-items {
        visibility: hidden;
        opacity: 0;
        -webkit-transition:
            visibility 0s linear 0.2s,
            opacity 0.2s linear;
        -moz-transition:
            visibility 0s linear 0.2s,
            opacity 0.2s linear;
        -ms-transition:
            visibility 0s linear 0.2s,
            opacity 0.2s linear;
        -o-transition:
            visibility 0s linear 0.2s,
            opacity 0.2s linear;
        transition:
            visibility 0s linear 0.2s,
            opacity 0.2s linear;
        &:hover {
            visibility: visible;
            opacity: 1;
            -webkit-transition-delay: 0s;
            -moz-transition-delay: 0s;
            -ms-transition-delay: 0s;
            -o-transition-delay: 0s;
            transition-delay: 0s;
        }
        max-width: 230px;
        width: auto;
        min-width: 230px;
        position: absolute;
        border-radius: 8px;
        padding-inline: 0;
        top: v-bind(top);
        z-index: 1000000;
        background-color: white;
        @include shadow(2);
        .sub-menu {
            padding: 0;
            border-radius: 8px;
            list-style-type: none;
        }
    }
}
</style>
